import { useEffect, useState } from "react";
import { FIELD_CLASS, LABEL_TEXT } from "../../ui/modalStyles";
import {
  getStoredSuperAdminReportLocationFilters,
  setStoredSuperAdminReportLocationFilters,
  SUPERADMIN_REPORT_LOCATION_FILTER_DEFAULTS,
} from "../../utils/superAdminReportLocationFilters";

export default function SuperAdminLocationFilters({
  onChange,
  disabled = false,
  autoApply = true,
}) {
  const [values, setValues] = useState(
    SUPERADMIN_REPORT_LOCATION_FILTER_DEFAULTS
  );
  const [appliedValues, setAppliedValues] = useState(
    SUPERADMIN_REPORT_LOCATION_FILTER_DEFAULTS
  );

  useEffect(() => {
    const stored = getStoredSuperAdminReportLocationFilters();
    setValues(stored);
    setAppliedValues(stored);
  }, []);

  const updateField = (key, nextValue) => {
    const next = {
      ...values,
      [key]: nextValue,
    };
    setValues(next);
    if (!autoApply) return;
    const stored = setStoredSuperAdminReportLocationFilters(next);
    setAppliedValues(stored);
    onChange?.(stored);
  };

  const toggleFilters = () => {
    const next = {
      ...values,
      enabled: !values.enabled,
    };
    setValues(next);
    if (autoApply || !next.enabled) {
      const stored = setStoredSuperAdminReportLocationFilters(next);
      setValues(stored);
      setAppliedValues(stored);
      onChange?.(stored);
    }
  };

  const applyFilters = () => {
    const next = setStoredSuperAdminReportLocationFilters({
      ...values,
      enabled: true,
    });
    setValues(next);
    setAppliedValues(next);
    onChange?.(next);
  };

  const clearFilters = () => {
    const next = setStoredSuperAdminReportLocationFilters({
      ...SUPERADMIN_REPORT_LOCATION_FILTER_DEFAULTS,
      enabled: values.enabled,
    });
    setValues(next);
    setAppliedValues(next);
    onChange?.(next);
  };

  const hasPendingChanges =
    values.enabled !== appliedValues.enabled ||
    values.city !== appliedValues.city ||
    values.province !== appliedValues.province ||
    values.postal_code !== appliedValues.postal_code ||
    values.country !== appliedValues.country;
  const fieldsDisabled = disabled || !values.enabled;

  return (
    <div className="rounded-2xl border border-slate-200 p-4 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="space-y-3">
          <div>
            <div className="text-sm font-semibold text-slate-900">
              Shared Location Filters
            </div>
            <div className="mt-1 text-xs text-slate-500">
              Applied across super admin reports and kept while you move between
              report pages.
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={toggleFilters}
              disabled={disabled}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold transition ${
                values.enabled
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "border-slate-200 bg-slate-50 text-slate-500"
              }`}
            >
              <span
                className={`h-2.5 w-2.5 rounded-full ${
                  values.enabled ? "bg-emerald-500" : "bg-slate-300"
                }`}
              />
              {values.enabled ? "Location Filters On" : "Location Filters Off"}
            </button>

            <button
              type="button"
              onClick={toggleFilters}
              disabled={disabled}
              className={`relative inline-flex h-7 w-12 items-center rounded-full border transition ${
                values.enabled
                  ? "border-emerald-500 bg-emerald-500"
                  : "border-slate-300 bg-slate-200"
              }`}
              aria-label={values.enabled ? "Turn location filters off" : "Turn location filters on"}
              aria-pressed={values.enabled}
            >
              <span
                className={`inline-block h-5 w-5 rounded-full bg-white shadow-sm transition ${
                  values.enabled ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>

            {hasPendingChanges ? (
              <div className="text-xs font-medium text-amber-600">
                Draft changes not applied yet
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <div>
          <label className={LABEL_TEXT}>City</label>
          <input
            type="text"
            value={values.city}
            onChange={(event) => updateField("city", event.target.value)}
            className={`${FIELD_CLASS} w-full`}
            placeholder="Ottawa"
            disabled={fieldsDisabled}
          />
        </div>
        <div>
          <label className={LABEL_TEXT}>Province / State</label>
          <input
            type="text"
            value={values.province}
            onChange={(event) => updateField("province", event.target.value)}
            className={`${FIELD_CLASS} w-full`}
            placeholder="Ontario"
            disabled={fieldsDisabled}
          />
        </div>
        <div>
          <label className={LABEL_TEXT}>Postal / ZIP</label>
          <input
            type="text"
            value={values.postal_code}
            onChange={(event) => updateField("postal_code", event.target.value)}
            className={`${FIELD_CLASS} w-full`}
            placeholder="K1G 0Z3"
            disabled={fieldsDisabled}
          />
        </div>
        <div>
          <label className={LABEL_TEXT}>Country</label>
          <input
            type="text"
            value={values.country}
            onChange={(event) => updateField("country", event.target.value)}
            className={`${FIELD_CLASS} w-full`}
            placeholder="Canada"
            disabled={fieldsDisabled}
          />
        </div>
      </div>

      <div className="flex items-center justify-end gap-3">
        {!autoApply ? (
          <button
            type="button"
            onClick={applyFilters}
            className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
            disabled={disabled || !values.enabled || !hasPendingChanges}
          >
            Apply Filters
          </button>
        ) : null}
        <button
          type="button"
          onClick={clearFilters}
          className="rounded-full border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          disabled={disabled}
        >
          Clear
        </button>
      </div>
    </div>
  );
}
